import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { SelectOption, ValidationItems } from '@kanda-libs/ks-component-ts';
import { EmploymentDetails } from '@kanda-libs/ks-frontend-services';

import get from 'lodash.get';

import { generateYearsOptions, getTime } from './helpers';
import { EmploymentClassType } from './types';

export interface EmploymentStatusFieldsHook {
  monthsName: string;
  yearsName: string;
  yearsOptions: SelectOption[];
  error: string | undefined;
  validation: ValidationItems;
}

export interface EmploymentStatusFieldsPrimary {
  type: Extract<EmploymentClassType, 'primary'>;
  employmentType: EmploymentDetails['employment_status'];
  index: undefined;
  arrayName: undefined;
}

export interface EmploymentStatusFieldsSecondary {
  type: Extract<EmploymentClassType, 'secondary'>;
  employmentType: EmploymentDetails['employment_status'];
  index: number;
  arrayName: string;
}

export type EmploymentStatusFieldsProps =
  | EmploymentStatusFieldsPrimary
  | EmploymentStatusFieldsSecondary;

export default function useEmploymentStatusFields({
  type,
  employmentType,
  index,
  arrayName,
}: EmploymentStatusFieldsProps): EmploymentStatusFieldsHook {
  const { setValue, formState, trigger } = useFormContext();

  const fieldName = useMemo(() => {
    if (type === 'primary')
      return `credit.employment_details.${employmentType}_details`;
    return `${arrayName}.${index}.${employmentType}_details`;
  }, [arrayName, index, type, employmentType]);

  const monthInEmploymentTypeName = useMemo(
    () => `${fieldName}.months_${employmentType}`,
    [fieldName, employmentType],
  );

  const monthsName = useMemo(
    () => `${fieldName}.placeholder.month_started`,
    [fieldName],
  );

  const yearsName = useMemo(
    () => `${fieldName}.placeholder.year_started`,
    [fieldName],
  );

  const yearsOptions = useMemo(() => generateYearsOptions(), []);

  const [years, months] = useWatch({
    name: [yearsName, monthsName],
  });

  const displayTypeName = useMemo(
    () => employmentType.split('_').join(' '),
    [employmentType],
  );

  const error = useMemo(() => {
    const heldError = get(formState.errors, monthInEmploymentTypeName);
    if (!heldError) return undefined;
    if (heldError?.type === 'required')
      return `You must provide the amount of time you have been ${displayTypeName} for`;
    if (heldError?.type === 'inPast')
      return `The date from which you have been ${displayTypeName} must be in the past`;
    return 'There is an error with this input';
  }, [formState, displayTypeName, monthInEmploymentTypeName]);

  const validation = useMemo(
    () => ({
      required: {
        value: true,
        message: `You must provide the amount of time you have been ${displayTypeName} for`,
      },
      validate: {
        value: {
          inPast: () => getTime(years, months) >= 0,
        },
        message: {
          inPast: `The date from which you have been ${displayTypeName} must be in the past`,
        },
      },
    }),
    [years, months, displayTypeName],
  );

  useEffect(() => {
    if (!years || !months) return;
    const time = getTime(years, months);
    setValue(monthInEmploymentTypeName, time);
    trigger(monthInEmploymentTypeName);
  }, [years, months, setValue, monthInEmploymentTypeName, type, trigger]);

  // Effect unsets value if error is present
  useEffect(() => {
    if (!error) return;
    setValue(monthInEmploymentTypeName, undefined);
  }, [error, monthInEmploymentTypeName, setValue]);

  return {
    monthsName,
    yearsName,
    yearsOptions,
    error,
    validation,
  };
}
